import OnetrustScripts from '../scripts/one-trust-scripts';
import ErrorBoundary from './_error';
import { InitializeDatadog } from '@analytics/datadog';
import { adobeDispatcher, datadogDispatcher } from '@analytics/dispatchers';
import { TrackUserAnalytics } from '@analytics/react';
import { AuthProvider } from '@exploration/auth';
import {
  AnalyticsFeatureFlagInitializer,
  ExplorationFlagsType,
  explorationFeatureFlagAtom,
  getDefaultFlags,
} from '@exploration/feature-management';
import { LiveChatScript } from '@exploration/live-chat';
import {
  HeadWithMetadata,
  PageInfo,
  ThemeProvider,
} from '@exploration/shared-components';
import { Layout } from '@exploration/shared-sections';
import {
  GlobalData,
  HeadlessPageData,
  NextPageWithLayout,
} from '@exploration/shared-types';
import { headlessPageDataAtom } from '@exploration/ui-utils';
import { headerFooterFeatureFlagAtom } from '@thrivent/thrivent-public-header-footer';
import { getCookie } from 'cookies-next';
import type { AppProps } from 'next/app';
import Head from 'next/head';
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- sharp is needed for image optimization and will get stripped out by nx if not imported.
import sharp from 'sharp';
import { MarketingParamsInitializer } from 'visitor-attributes-react';

if (process.env.NEXT_PUBLIC_API_MOCKING === 'enabled') {
  require('../mocks');
}

type AppPropsWithLayout<P = unknown> = AppProps<P> & {
  Component: NextPageWithLayout<P>;
};

function CustomApp({
  Component,
  pageProps,
}: AppPropsWithLayout<
  { pageProps: Record<string, unknown> } & { flags?: ExplorationFlagsType } & {
    globalData?: GlobalData;
  } & HeadlessPageData & {
      source?: {
        env?: string;
        id?: string | null;
        path?: string | null;
        fallback?: string;
      };
    }
>) {
  const getLayout =
    Component.getLayout ??
    ((page) => {
      return (
        <>
          <OnetrustScripts />
          <InitializeDatadog
            rumUser={{
              id: (getCookie('ANONYMOUS_USER_ID') as string) ?? 'default',
            }}
            featureFlags={flags}
          />
          <AuthProvider>
            <ThemeProvider
              jotaiValues={[
                [explorationFeatureFlagAtom, flags],
                [headerFooterFeatureFlagAtom, flags],
                [headlessPageDataAtom, headlessPageData],
              ]}
            >
              <TrackUserAnalytics
                dispatchers={[adobeDispatcher, datadogDispatcher]}
              >
                <MarketingParamsInitializer />
                <AnalyticsFeatureFlagInitializer />
                <ErrorBoundary>
                  <Layout globalData={page.props.globalData}>
                    <HeadWithMetadata metadata={page.props.metadata} />
                    <main>{page}</main>
                  </Layout>
                </ErrorBoundary>
                <>
                  <PageInfo source={pageProps?.source} />
                  {flags['show-live-chat'] && <LiveChatScript />}
                </>
              </TrackUserAnalytics>
            </ThemeProvider>
          </AuthProvider>
        </>
      );
    });

  const flags = pageProps?.flags ?? getDefaultFlags();

  const headlessPageData = {
    metadata: pageProps?.metadata,
    text: pageProps?.text,
    image: pageProps?.image,
    callToAction: pageProps?.callToAction,
    list: pageProps?.list,
    video: pageProps?.video,
  };

  return (
    <>
      <Head>
        <meta charSet="UTF-8" />
        <meta name="viewport" content="initial-scale=1.0, width=device-width" />
        <link rel="icon" href="/favicon.ico" key="favicon" />
      </Head>
      {getLayout(<Component {...pageProps} />)}
    </>
  );
}

export default CustomApp;
